/**
 * The client-resync protocol — `event_seq` (charter §2.8, plan §3.3).
 *
 * One of Phase N's two named risks, and the module every domain's realtime path
 * will depend on. It exists to make ordering and gaps THE SAME PROBLEM: a
 * broadcast is applied only when it is exactly the next sequence, and anything
 * else — out of order, missed while disconnected, missed while the tab was
 * hidden — resolves through one fetch-since path.
 *
 * Deliberately dependency-injected and free of any Supabase import. The gate
 * requires each branch to be OBSERVED firing, not inferred (the realtime spike's
 * round 2 recorded "zero loss" from a script that never measured loss), and a
 * module that takes its fetcher as an argument can be driven into every branch
 * deterministically. The live wiring lives in realtime.js.
 *
 * `lastSeq` is memory-only, per charter §12 C8: a tab resuming a stale sequence
 * from localStorage is a failure mode we would be inventing for ourselves.
 */

/**
 * Outcomes, so callers and tests name the same things.
 *
 * There is no APPLIED outcome: no signal can be applied without a fetch,
 * because the emitter does not put the events in the signal. See onSignal.
 */
export const IGNORED_STALE = "ignored-stale";
export const FETCHED = "fetched";
export const FULL_INVALIDATE = "full-invalidate";

/**
 * @param {object} opts
 * @param {(afterSeq: number, limit: number) => Promise<Array<{seq:number,topic:string,entity_id:string|null}>>} opts.fetchEventsSince
 *   Backed by the `events_since` RPC. Must return rows ordered by seq.
 * @param {(events: Array<object>) => void} opts.onInvalidate       — apply these specific changes.
 * @param {() => void} opts.onFullInvalidate                        — drop everything and refetch.
 * @param {(gap: {fromSeq:number, oldestSeq:number, missing:number}) => void} [opts.onGap]
 *   Fired the moment the unrecoverable gap is DETECTED, before the drain and
 *   before onFullInvalidate.
 *
 *   This callback is the whole reason the branch above it is not
 *   self-deceiving. The header says each branch has to be OBSERVED firing
 *   rather than inferred, and the only symptom a full invalidate produces from
 *   the outside is one extra org_bootstrap round trip — indistinguishable from
 *   an ordinary refetch. `fetchSince`'s return value carries the outcome, but
 *   nothing awaits it: realtime.js's three call sites are all fire-and-forget.
 *   So without this, "how often are clients falling off the event sequence, for
 *   which orgs, and how big was the gap?" — the measurement that decides
 *   whether the `org_events` retention window is long enough — has no answer.
 *
 *   Injected rather than imported so this module stays free of Supabase AND of
 *   Sentry; the wiring is in useOrgRefdata.js, next to the org id.
 * @param {number} [opts.limit=500]  Page size, capped at 1000.
 */
export function createResync({
  fetchEventsSince,
  onInvalidate,
  onFullInvalidate,
  onGap,
  limit = 500,
}) {
  let lastSeq = 0;
  // Fetches are serialised. Two overlapping fetch-since calls would interleave
  // their applies and could move lastSeq backwards — which is exactly the
  // "missed event" this module exists to prevent, self-inflicted.
  let inFlight = null;
  // The ONE re-run every caller that arrived mid-fetch is waiting on. See
  // fetchSinceSerialised.
  let queued = null;

  // `events_since` silently clamps its own LIMIT to 1000 (0003:131). The client
  // decides it is caught up with `rows.length < pageSize`, so a pageSize the RPC
  // will never honour makes a FULL page look like a short one and the rest of
  // the backlog is left unapplied. Clamp to the same number the server does, so
  // the two can never disagree.
  const pageSize = Math.min(Math.max(Number(limit) || 500, 1), 1000);

  const setBaseline = (seq) => { lastSeq = Number(seq) || 0; };

  async function fetchSince() {
    let count = 0;
    let unrecoverable = false;

    for (;;) {
      const rows = await fetchEventsSince(lastSeq, pageSize);
      if (!rows || rows.length === 0) break;

      // The unrecoverable gap: the oldest row we can still be given is already
      // newer than the next one we needed, so events between have been pruned.
      // Detected here rather than assumed away — plan §3.3 requires the branch
      // to exist, and H3 requires it to be seen firing.
      if (rows[0].seq > lastSeq + 1 && !unrecoverable) {
        unrecoverable = true;
        // Reported here, not after the drain: `lastSeq` is about to be advanced
        // to the end of the backlog, and the gap is the distance from where we
        // WERE. Reading it afterwards would report a gap of zero every time.
        onGap?.({
          fromSeq: lastSeq,
          oldestSeq: rows[0].seq,
          missing: rows[0].seq - lastSeq - 1,
        });
      }

      // APPLY THE PAGE, THEN ADVANCE. Accumulating every page and applying the
      // lot at the end means a later page's rejection (token refresh, network
      // blip) discards every event already read while the cursor sits past
      // them — no future fetch can return them, because `after_seq` has moved,
      // and nothing reports it. Per-page, a mid-run failure leaves the cursor
      // exactly where the applies got to.
      //
      // Once the gap is declared we deliberately stop applying and keep
      // draining: onFullInvalidate() refetches everything anyway, and the
      // cursor must still end at the org's max or the very next signal would
      // re-enter this branch forever.
      if (!unrecoverable) { onInvalidate(rows); count += rows.length; }
      lastSeq = rows[rows.length - 1].seq;

      if (rows.length < pageSize) break;   // short page => caught up
    }

    if (unrecoverable) {
      onFullInvalidate();
      return { outcome: FULL_INVALIDATE, lastSeq, count };
    }
    return { outcome: FETCHED, lastSeq, count };
  }

  function runFetch() {
    const run = fetchSince().finally(() => { if (inFlight === run) inFlight = null; });
    inFlight = run;
    return run;
  }

  /**
   * Serialise WITHOUT joining.
   *
   * Handing a mid-flight caller the running fetch looks like free
   * deduplication and is the silent-staleness bug this whole module exists to
   * prevent. The running fetch's `events_since` query may have executed
   * SERVER-SIDE before the event that prompted the new call committed: the
   * joiner then resolves having never asked for its own event, the cursor has
   * moved past it, and nothing reschedules. If that event is the org's last
   * change — one edit, then quiet, the common case — the tab is stale for the
   * rest of the session with no error.
   *
   * So everyone arriving mid-fetch waits on ONE re-run that starts after the
   * current one finishes. One extra round trip per burst, and it returns zero
   * rows when there was genuinely nothing new.
   */
  function fetchSinceSerialised() {
    if (!inFlight) return runFetch();
    queued ??= inFlight
      // A failed run must still be followed by the re-run — the joiner's event
      // is no less real for the previous fetch having thrown.
      .catch(() => {})
      .then(() => { queued = null; return runFetch(); });
    return queued;
  }

  return {
    get lastSeq() { return lastSeq; },

    /** Seed from org_bootstrap's `seq`, captured before its slices were read. */
    setBaseline,

    /**
     * Reconcile the cursor against the authoritative seq a LATER bootstrap
     * carried. Returns true if the cursor was moved.
     *
     * The baseline used to be set exactly once, at mount, and nothing ever
     * re-seeded it. If the server's `org_counters.event_seq` moves BACKWARDS —
     * a db-restore of an older snapshot, a PITR, a failover — every subsequent
     * signal has `max_seq < lastSeq`, so onSignal calls it IGNORED_STALE and
     * every resume fetches zero rows. The tab renders stale data for the rest
     * of the session with no error and no recovery short of a reload. Every
     * bootstrap response carries the current seq, so any refetch is a chance to
     * notice.
     *
     * BACKWARDS ONLY, deliberately. Jumping the cursor FORWARD to a bootstrap's
     * seq would skip whatever happened in between, and `entry.changed` maps to
     * no refdata slice — so a forward jump would silently drop ledger
     * invalidations that this store does not consume but N3's does.
     */
    reconcileBaseline(seq) {
      const next = Number(seq) || 0;
      if (next >= lastSeq) return false;
      lastSeq = next;
      return true;
    },

    /**
     * A coalesced signal arrived. It carries the org's max_seq, so it is a
     * statement about where the org has got to, not a delivery of the events
     * themselves.
     */
    async onSignal(signal) {
      const maxSeq = Number(signal?.max_seq ?? 0);

      // Already seen. Duplicate delivery is normal on reconnect and must be a
      // no-op, not a refetch storm.
      if (maxSeq <= lastSeq) return { outcome: IGNORED_STALE, lastSeq };

      // EVERY signal costs one events_since call, on purpose.
      //
      // There was an APPLIED fast path here that skipped the round trip when a
      // signal was exactly the next sequence AND carried the event itself. It
      // could never fire: the only emitter, t3_internal.emit_org_signal
      // (0005:82-89), sends {org_id, max_seq, topics, entities} and there is no
      // `events` key in it — so the branch was dead code proved only by a unit
      // test that constructed a payload the server never sends. A dead branch
      // that reads as an optimisation is worse than no optimisation: it hides
      // the actual cost of a signal from anyone budgeting round trips.
      //
      // Reviving it means changing the emitter (send the single event when the
      // pending set has exactly one entry and pending_max_seq = seq), not
      // changing this file.
      return fetchSinceSerialised();
    },

    /**
     * The three triggers plan §3.3 names: channel SUBSCRIBED, tab becoming
     * visible, and socket error/reconnect. All of them mean the same thing:
     * we may have missed something, so ask.
     */
    resume: () => fetchSinceSerialised(),
  };
}
